import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { CompanySideBar } from '../components/CompanySideBar';
import { getCompanyProfile, updateCompanyProfile } from '../services/CompanyProfileService';
import { getCountries, getCitiesByCountry } from '../services/SelectorsService';
import { useAuthStore } from '../store';
import { LogoSpinner } from '../components/LogoSpinner';
import swal from 'sweetalert';

// Schema de validación
const companyProfileSchema = yup.object().shape({
    razonSocial: yup.string()
        .nullable()
        .min(2, 'La razón social debe tener al menos 2 caracteres')
        .max(200, 'La razón social no puede exceder 200 caracteres'),
    rnc: yup.string()
        .nullable()
        .test('rnc-format', 'El RNC debe contener solo números y tener entre 8 y 15 dígitos', function (value) {
            if (!value) return true; // Opcional
            return /^[0-9]{8,15}$/.test(value);
        }),
    telefono: yup.string()
        .nullable()
        .matches(/^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/, 'Formato de teléfono inválido'),
    direccion: yup.string().nullable(),
    sitioWeb: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    fechaFundacion: yup.string()
        .nullable()
        .test('is-valid-date', 'Fecha inválida', function (value) {
            if (!value) return true;
            const date = new Date(value);
            if (isNaN(date.getTime())) return false;
            return date <= new Date();
        }),
    pais: yup.string().nullable(),
    ciudad: yup.string().nullable(),
    descripcion: yup.string()
        .nullable()
        .max(2000, 'La descripción no puede exceder 2000 caracteres'),
    facebook: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    x: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    instagram: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
    linkedin: yup.string()
        .nullable()
        .url('Debe ser una URL válida'),
});

export function CompanyProfile() {
    const queryClient = useQueryClient();
    const { auth } = useAuthStore();
    const [selectedCountryId, setSelectedCountryId] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(companyProfileSchema),
        defaultValues: {
            razonSocial: '',
            rnc: '',
            telefono: '',
            direccion: '',
            sitioWeb: '',
            fechaFundacion: '',
            pais: '',
            ciudad: '',
            descripcion: '',
            facebook: '',
            x: '',
            instagram: '',
            linkedin: '',
        },
    });

    const watchedPais = watch('pais');

    // Query para obtener el perfil de la empresa
    const { data: profileData, isLoading: isLoadingProfile, error: profileError } = useQuery(
        'companyProfile',
        async () => {
            const response = await getCompanyProfile();
            return response.data;
        },
        {
            refetchOnMount: true, // Forzar refetch al montar el componente
        }
    );

    // useEffect para actualizar el formulario cuando profileData cambie (incluyendo datos del caché)
    useEffect(() => {
        if (profileData && !isLoadingProfile) {
            // Mapear datos del API al formulario
            let fechaFundacion = null;
            if (profileData.fechaFundacion) {
                // Convertir fecha ISO a formato YYYY-MM-DD para el input date
                const date = new Date(profileData.fechaFundacion);
                if (!isNaN(date.getTime())) {
                    fechaFundacion = date.toISOString().split('T')[0];
                }
            }

            // Mapear país y ciudad: el API puede devolver nombres o IDs
            // Necesitamos encontrar el ID del país si viene como nombre
            let paisId = null;
            let ciudadId = null;

            if (profileData.pais) {
                // Si el país viene como objeto, usar el ID
                if (typeof profileData.pais === 'object' && profileData.pais.id) {
                    paisId = profileData.pais.id;
                } else if (typeof profileData.pais === 'string') {
                    // Si viene como string, puede ser un nombre o un ID
                    // Intentar buscar el país por nombre en la lista cuando esté disponible
                    // Por ahora, guardar el valor y se procesará cuando se carguen los países
                    paisId = profileData.pais;
                }
            }

            if (profileData.ciudad) {
                if (typeof profileData.ciudad === 'object' && profileData.ciudad.id) {
                    ciudadId = profileData.ciudad.id;
                } else if (typeof profileData.ciudad === 'string') {
                    ciudadId = profileData.ciudad;
                }
            }

            // Establecer país seleccionado primero para cargar ciudades
            // Si es un nombre, necesitamos buscar el ID cuando se carguen los países
            if (paisId) {
                // Si parece un UUID, usarlo directamente; si no, será procesado en otro useEffect
                if (paisId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                    setSelectedCountryId(paisId);
                } else {
                    // Es un nombre, buscar el ID cuando se carguen los países
                    // Esto se manejará en un useEffect
                }
            }

            reset({
                razonSocial: profileData.razonSocial || '',
                rnc: profileData.rnc || profileData.ruc || '', // Mantener compatibilidad con backend antiguo
                telefono: profileData.telefono || '',
                direccion: profileData.direccion || '',
                sitioWeb: profileData.sitioWeb || '',
                fechaFundacion: fechaFundacion || '',
                pais: paisId || '',
                ciudad: ciudadId || '',
                descripcion: profileData.descripcion || '',
                facebook: profileData.facebook || '',
                x: profileData.x || '',
                instagram: profileData.instagram || '',
                linkedin: profileData.linkedin || '',
            });
        }
    }, [profileData, isLoadingProfile, reset]);

    // Query para obtener países
    const { data: countriesData, isLoading: isLoadingCountries } = useQuery(
        'countries',
        async () => {
            const response = await getCountries();
            return response.data;
        },
        {
            onSuccess: (countries) => {
                // Si el país en el formulario es un nombre, buscar el ID
                const currentPais = watch('pais');
                if (currentPais && countries && !currentPais.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                    const country = countries.find(c => c.nombre === currentPais);
                    if (country) {
                        setValue('pais', country.id);
                        setSelectedCountryId(country.id);
                    }
                }
            },
        }
    );

    // Query para obtener ciudades según el país seleccionado
    const { data: citiesData, isLoading: isLoadingCities } = useQuery(
        ['cities', selectedCountryId],
        async () => {
            if (!selectedCountryId) return [];
            const response = await getCitiesByCountry(selectedCountryId);
            return response.data;
        },
        {
            enabled: !!selectedCountryId,
        }
    );

    // Efecto para establecer la ciudad cuando las ciudades se cargan y hay un valor guardado
    useEffect(() => {
        if (citiesData && citiesData.length > 0 && selectedCountryId) {
            const currentCiudadId = watch('ciudad');
            if (currentCiudadId) {
                // Verificar que la ciudad existe en la lista cargada
                const ciudadExists = citiesData.some(city => city.id === currentCiudadId);
                if (ciudadExists) {
                    // Asegurarse de que el valor se mantenga en el formulario
                    setValue('ciudad', currentCiudadId, { shouldValidate: false });
                }
            }
        }
    }, [citiesData, selectedCountryId, watch, setValue]);

    // Efecto para resetear ciudad cuando cambia el país (solo cuando el usuario cambia el país manualmente)
    useEffect(() => {
        // Solo ejecutar si el país cambió y no es la carga inicial
        if (watchedPais && watchedPais !== selectedCountryId) {
            setSelectedCountryId(watchedPais);
            // Resetear ciudad cuando el país cambia manualmente
            setValue('ciudad', '');
        } else if (!watchedPais) {
            setSelectedCountryId(null);
            setValue('ciudad', '');
        }
    }, [watchedPais, setValue, selectedCountryId]);

    // Mutation para actualizar el perfil
    const updateProfileMutation = useMutation(
        async (data) => {
            // Preparar datos para el API - ahora se envían paisId y ciudadId
            const apiData = {
                razonSocial: data.razonSocial || null,
                ruc: data.rnc || null,
                telefono: data.telefono || null,
                direccion: data.direccion || null,
                sitioWeb: data.sitioWeb || null,
                fechaFundacion: data.fechaFundacion || null,
                paisId: data.pais || null,
                ciudadId: data.ciudad || null,
                descripcion: data.descripcion || null,
                facebook: data.facebook || null,
                x: data.x || null,
                instagram: data.instagram || null,
                linkedin: data.linkedin || null,
            };

            return await updateCompanyProfile(apiData);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('companyProfile');
                swal('Éxito', 'Perfil de empresa actualizado correctamente', 'success');
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.message || error.message || 'Error al actualizar el perfil';
                swal('Error', errorMessage, 'error');
            },
        }
    );

    const onSubmit = (data) => {
        updateProfileMutation.mutate(data);
    };

    const isLoading = isLoadingProfile || isLoadingCountries;
    const hasError = profileError;

    if (isLoading) {
        return <LogoSpinner />;
    }

    if (hasError) {
        return (
            <>
                <div className="page-content bg-white">
                    <div className="content-block">
                        <div className="section-full bg-white p-t50 p-b20">
                            <div className="container">
                                <div className="row">
                                    <CompanySideBar />
                                    <div className="col-xl-9 col-lg-8 m-b30">
                                        <div className="job-bx submit-resume">
                                            <div className="alert alert-danger" role="alert">
                                                <h4 className="alert-heading">Error al cargar el perfil</h4>
                                                <p>{profileError?.response?.data?.message || profileError?.message || 'Ha ocurrido un error al cargar el perfil de empresa. Por favor, intenta de nuevo.'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="page-content bg-white">
                <div className="content-block">
                    <div className="section-full bg-white p-t50 p-b20">
                        <div className="container">
                            <div className="row">
                                <CompanySideBar />
                                <div className="col-xl-9 col-lg-8 m-b30">
                                    <div className="job-bx submit-resume">
                                        <div className="job-bx-title clearfix">
                                            <h5 className="font-weight-700 pull-left text-uppercase">Perfil de Compañia</h5>
                                            <Link to={"/company-profile"} className="site-button right-arrow button-sm float-right">Atrás</Link>
                                        </div>

                                        {/* Alerta de cuenta pendiente */}
                                        {auth.user?.estado === 'PENDIENTE' && (
                                            <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                                <strong><i className="fa fa-exclamation-triangle"></i> Cuenta Pendiente de Aprobación</strong>
                                                <p className="mb-0 mt-2">
                                                    Tu cuenta de empresa está pendiente de aprobación por parte de nuestro equipo.
                                                    Mientras tanto, solo puedes ver vacantes y editar tu perfil.
                                                    Una vez aprobada, tendrás acceso completo a todas las funcionalidades.
                                                </p>
                                            </div>
                                        )}

                                        <form onSubmit={handleSubmit(onSubmit)}>
                                            <div className="row m-b30">
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Razón Social:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.razonSocial ? 'is-invalid' : ''}`}
                                                            placeholder="Introduce la razón social de la compañia"
                                                            {...register('razonSocial')}
                                                        />
                                                        {errors.razonSocial && (
                                                            <div className="invalid-feedback">{errors.razonSocial.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>RNC:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.rnc ? 'is-invalid' : ''}`}
                                                            placeholder="12345678901"
                                                            {...register('rnc')}
                                                        />
                                                        {errors.rnc && (
                                                            <div className="invalid-feedback">{errors.rnc.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Teléfono:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.telefono ? 'is-invalid' : ''}`}
                                                            placeholder="+1 123 456 7890"
                                                            {...register('telefono')}
                                                        />
                                                        {errors.telefono && (
                                                            <div className="invalid-feedback">{errors.telefono.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Sitio Web:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.sitioWeb ? 'is-invalid' : ''}`}
                                                            placeholder="https://www.empresa.com"
                                                            {...register('sitioWeb')}
                                                        />
                                                        {errors.sitioWeb && (
                                                            <div className="invalid-feedback">{errors.sitioWeb.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Fecha de Fundación:</label>
                                                        <input
                                                            type="date"
                                                            className={`form-control ${errors.fechaFundacion ? 'is-invalid' : ''}`}
                                                            {...register('fechaFundacion')}
                                                        />
                                                        {errors.fechaFundacion && (
                                                            <div className="invalid-feedback">{errors.fechaFundacion.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Dirección:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.direccion ? 'is-invalid' : ''}`}
                                                            placeholder="Av. Principal 456"
                                                            {...register('direccion')}
                                                        />
                                                        {errors.direccion && (
                                                            <div className="invalid-feedback">{errors.direccion.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>País:</label>
                                                        <Form.Control
                                                            as="select"
                                                            custom
                                                            className={`custom-select ${errors.pais ? 'is-invalid' : ''}`}
                                                            {...register('pais')}
                                                            disabled={isLoadingCountries}
                                                        >
                                                            <option value="">Seleccione un país</option>
                                                            {countriesData?.map((country) => (
                                                                <option key={country.id} value={country.id}>
                                                                    {country.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                        {errors.pais && (
                                                            <div className="invalid-feedback">{errors.pais.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Ciudad:</label>
                                                        <Form.Control
                                                            as="select"
                                                            custom
                                                            className={`custom-select ${errors.ciudad ? 'is-invalid' : ''}`}
                                                            {...register('ciudad')}
                                                            disabled={!selectedCountryId || isLoadingCities}
                                                        >
                                                            <option value="">Seleccione una ciudad</option>
                                                            {citiesData?.map((city) => (
                                                                <option key={city.id} value={city.id}>
                                                                    {city.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                        {errors.ciudad && (
                                                            <div className="invalid-feedback">{errors.ciudad.message}</div>
                                                        )}
                                                        {!selectedCountryId && (
                                                            <small className="form-text text-muted">Primero seleccione un país</small>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 col-md-12">
                                                    <div className="form-group">
                                                        <label>Descripción:</label>
                                                        <textarea
                                                            className={`form-control ${errors.descripcion ? 'is-invalid' : ''}`}
                                                            placeholder="Descripción de la empresa"
                                                            rows="5"
                                                            {...register('descripcion')}
                                                        />
                                                        {errors.descripcion && (
                                                            <div className="invalid-feedback">{errors.descripcion.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="job-bx-title clearfix">
                                                <h5 className="font-weight-700 pull-left text-uppercase">Redes Sociales</h5>
                                            </div>
                                            <div className="row">
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Facebook:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.facebook ? 'is-invalid' : ''}`}
                                                            placeholder="https://www.facebook.com/empresa"
                                                            {...register('facebook')}
                                                        />
                                                        {errors.facebook && (
                                                            <div className="invalid-feedback">{errors.facebook.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>X (Twitter):</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.x ? 'is-invalid' : ''}`}
                                                            placeholder="https://x.com/empresa"
                                                            {...register('x')}
                                                        />
                                                        {errors.x && (
                                                            <div className="invalid-feedback">{errors.x.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Instagram:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.instagram ? 'is-invalid' : ''}`}
                                                            placeholder="https://www.instagram.com/empresa"
                                                            {...register('instagram')}
                                                        />
                                                        {errors.instagram && (
                                                            <div className="invalid-feedback">{errors.instagram.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>LinkedIn:</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.linkedin ? 'is-invalid' : ''}`}
                                                            placeholder="https://www.linkedin.com/company/empresa"
                                                            {...register('linkedin')}
                                                        />
                                                        {errors.linkedin && (
                                                            <div className="invalid-feedback">{errors.linkedin.message}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {updateProfileMutation.isError && (
                                                <div className="alert alert-danger" role="alert">
                                                    Error al guardar el perfil. Por favor, intenta de nuevo.
                                                </div>
                                            )}

                                            <button
                                                type="submit"
                                                className="site-button m-b30"
                                                disabled={isSubmitting || updateProfileMutation.isLoading}
                                            >
                                                {isSubmitting || updateProfileMutation.isLoading ? 'Guardando...' : 'Guardar'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
